import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Paper, Typography, Button, Chip, Divider,
  CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox,
  Switch, Tooltip, Pagination, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, Alert,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import PlaylistAddOutlinedIcon from "@mui/icons-material/PlaylistAddOutlined";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import LayersIcon from "@mui/icons-material/Layers";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import { layer_api_path } from "../api_registry";

// ─── constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

// ─── small helpers ────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const cfg = {
    Success: { color: "success", label: "Success" },
    Processing: { color: "warning", label: "Processing" },
    Failed: { color: "error", label: "Failed" },
  }[status] ?? { color: "default", label: status ?? "—" };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

function LogIcon({ status }) {
  if (status === "success") return <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />;
  if (status === "error") return <ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />;
  return <HourglassEmptyIcon sx={{ fontSize: 18, color: "text.disabled" }} />;
}

// ─── api ──────────────────────────────────────────────────────────────────────

async function apiFetch(url, options = {}) {
  const res = await fetch(url, {
    headers: { Accept: "application/json", ...options.headers },
    ...options,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res;
}

// ─── main component ───────────────────────────────────────────────────────────

export default function LayerManager({
  project,
  openPopup,
  setOpenPopup,
  onNewLayerCallback,
  onLayerRemovedCallback,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [layerData, setLayerData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalitems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);

  // per-row async states
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [deletingIds, setDeletingIds] = useState([]);
  const [togglingIds, setTogglingIds] = useState([]);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);

  // handset editable map  { [layerId]: bool }
  const [editableMap, setEditableMap] = useState({});

  // upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadLogs, setUploadLogs] = useState([]);   // [{ fileName, fileSize, lines:[{level,message}], status }]
  const logEndRef = useRef(null);

  // ── fetch layers ────────────────────────────────────────────────────────────

  const fetchLayers = useCallback(async (pid, pg) => {
    setTableLoading(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${layer_api_path}/${pid}/?page_size=${PAGE_SIZE}&page=${pg}`
      );
      const data = await res.json();
      setTotalPages(Math.max(1, Math.ceil(data.total / PAGE_SIZE)));
      setLayerData(data.items);
      setTotalItems(data.total);

      // sync editable map
      setEditableMap(
        Object.fromEntries(data.items.map((l) => [l.id, l.editable_from_handset ?? false]))
      );
      setSelectedIds([]);
    } catch (err) {
      setError("Failed to load layers. Please try again.");
    } finally {
      setTableLoading(false);
    }
  }, []);

  // fetch on open / page change
  useEffect(() => {
    if (openPopup && project?.id) {
      fetchLayers(project.id, page);
    }
  }, [openPopup, project?.id, page, fetchLayers]);

  // reset page to 1 when dialog opens
  useEffect(() => {
    if (openPopup) setPage(1);
  }, [openPopup]);

  // auto-scroll log to bottom
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [uploadLogs]);

  // ── add / remove from map ───────────────────────────────────────────────────

  const addLayerToMap = async (layerId) => {
    setAddingId(layerId);
    try {
      const res = await apiFetch(
        `${layer_api_path}/${project.id}/${layerId}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ layer_added: true, return_object : true }) }
      );
      const data = await res.json();
      onNewLayerCallback(data);
      setLayerData((prev) => prev.map((l) => l.id === layerId ? { ...l, layer_added: true } : l));
    } catch {
      setError("Failed to add layer to map.");
    } finally {
      setAddingId(null);
    }
  };

  const removeLayerFromMap = async (layerId) => {
    setRemovingId(layerId);
    try {
      await apiFetch(
        `${layer_api_path}/${project.id}/${layerId}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ layer_added: false }) }
      );
      setLayerData((prev) => prev.map((l) => l.id === layerId ? { ...l, layer_added: false } : l));
      onLayerRemovedCallback?.(layerId);
    } catch {
      setError("Failed to remove layer from map.");
    } finally {
      setRemovingId(null);
    }
  };

  // ── delete ──────────────────────────────────────────────────────────────────

  const deleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeletingIds(selectedIds);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          apiFetch(`${layer_api_path}/${project.id}/${id}`, { method: "DELETE" })
        )
      );
      setLayerData((prev) => prev.filter((l) => !selectedIds.includes(l.id)));
      setEditableMap((prev) => {
        const next = { ...prev };
        selectedIds.forEach((id) => delete next[id]);
        return next;
      });
      setSelectedIds([]);
    } catch {
      setError("Failed to delete one or more layers.");
    } finally {
      setDeletingIds([]);
    }
  };

  // ── editable toggle ─────────────────────────────────────────────────────────

  const handleEditableToggle = async (layerId) => {
    const prev = editableMap[layerId] ?? false;
    const newValue = !prev;

    setTogglingIds((p) => [...p, layerId]);
    setEditableMap((p) => ({ ...p, [layerId]: newValue }));   // optimistic

    try {
      await apiFetch(`${layer_api_path}/${project.id}/${layerId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ editable_from_handset: newValue }),
      });
    } catch {
      setEditableMap((p) => ({ ...p, [layerId]: prev }));     // revert
      setError("Failed to update editable status.");
    } finally {
      setTogglingIds((p) => p.filter((id) => id !== layerId));
    }
  };

  // ── selection helpers ───────────────────────────────────────────────────────

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleSelectAll = () =>
    setSelectedIds(
      selectedIds.length === layerData.length ? [] : layerData.map((l) => l.id)
    );

  // ── upload ──────────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    setFile(e.target.files[0] ?? null);
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!file || !project?.id || uploading) return;

    const entry = {
      fileName: file.name,
      fileSize: `${(file.size / 1024).toFixed(1)} KB`,
      startedAt: new Date().toLocaleTimeString(),
      lines: [],   // [{ level: "info"|"error", message: string }]
      status: null,
    };

    setUploadLogs((prev) => [entry, ...prev]);
    setUploading(true);

    const pushLine = (level, message) => {
      entry.lines = [...entry.lines, { level, message }];
      setUploadLogs((prev) => [{ ...entry }, ...prev.slice(1)]);
    };

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await apiFetch(`${layer_api_path}/${project.id}/upload_data`, {
        method: "POST",
        headers: {},   // let browser set multipart boundary
        body: formData,
      });

      // ── consume NDJSON stream ──────────────────────────────────────────────
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();   // keep incomplete line

        for (const raw of lines) {
          const trimmed = raw.trim();
          if (!trimmed) continue;
          try {
            const { level = "info", message = trimmed } = JSON.parse(trimmed);
            pushLine(level, message);
          } catch {
            pushLine("info", trimmed);
          }
        }
      }

      entry.status = "success";
      setUploadLogs((prev) => [{ ...entry }, ...prev.slice(1)]);

      // refresh layer list
      await fetchLayers(project.id, page);
    } catch (err) {
      pushLine("error", err.message ?? "Upload failed");
      entry.status = "error";
      setUploadLogs((prev) => [{ ...entry }, ...prev.slice(1)]);
    } finally {
      setFile(null);
      setUploading(false);
    }
  };

  // ── close ───────────────────────────────────────────────────────────────────

  const handleClose = () => {
    if (uploading) return;   // block close during active upload
    setOpenPopup(false);
  };

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <Dialog open={openPopup} onClose={handleClose} maxWidth="md" fullWidth>

      {/* ── Title ── */}
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6" fontWeight={600} component="span">Manage Map Layers</Typography>
        <Tooltip title={uploading ? "Upload in progress" : "Close"}>
          <span>
            <Button onClick={handleClose} disabled={uploading} sx={{ minWidth: 0, color: "text.secondary" }}>
              <CloseIcon />
            </Button>
          </span>
        </Tooltip>
      </DialogTitle>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Upload" sx={{ textTransform: "none" }} />
          <Tab label={`Layers (${totalitems})`} sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      {/* ── Content ── */}
      <DialogContent sx={{ height: "68vh", display: "flex", flexDirection: "column", p: 2, gap: 1.5, overflow: "hidden" }}>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ flexShrink: 0 }}>
            {error}
          </Alert>
        )}

        {/* ════ UPLOAD TAB ════ */}
        {activeTab === 0 && (
          <>
            {/* file picker row */}
            <Box sx={{ display: "flex", alignItems: "center", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden", flexShrink: 0 }}>
              <Button variant="contained" component="label" disableElevation sx={{ borderRadius: 0, px: 2.5, flexShrink: 0 }}>
                Choose file
                <input hidden type="file" accept=".kml,.zip,.geojson,.gpkg" onChange={handleFileChange} />
              </Button>

              <Typography variant="body2" color={file ? "text.primary" : "text.secondary"} sx={{ flex: 1, px: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {file?.name ?? "No file chosen"}
              </Typography>

              <Button
                variant="outlined"
                onClick={handleUpload}
                disabled={!file || uploading}
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                sx={{ borderRadius: 0, px: 2.5, borderLeft: 1, borderColor: "divider", flexShrink: 0 }}
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </Box>

            {/* log history */}
            <Paper variant="outlined" sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="subtitle2" fontWeight={600}>Upload History</Typography>
                <Typography variant="caption" color="text.secondary">{uploadLogs.length} upload{uploadLogs.length !== 1 ? "s" : ""}</Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
                {uploadLogs.length === 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "text.disabled", gap: 1 }}>
                    <UploadIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                    <Typography variant="body2">No uploads yet</Typography>
                  </Box>
                ) : (
                  uploadLogs.map((log, i) => (
                    <Box key={i}>
                      {/* log header */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                        <LogIcon status={log.status} />
                        <Typography variant="body2" fontWeight={500}>{log.fileName}</Typography>
                        <Typography variant="caption" color="text.secondary">({log.fileSize})</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ ml: "auto" }}>{log.startedAt}</Typography>
                      </Box>

                      {/* log lines */}
                      <Box sx={{
                        ml: 3.5, mb: 1,
                        bgcolor: "grey.50", border: 1, borderColor: "grey.200",
                        borderRadius: 1, p: 1.5,
                        fontFamily: "monospace", fontSize: "0.72rem", lineHeight: 1.6,
                      }}>
                        {log.lines.length === 0 && log.status === null ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                            <CircularProgress size={12} /> streaming…
                          </Box>
                        ) : (
                          log.lines.map((line, j) => (
                            <Box key={j} sx={{ color: line.level === "error" ? "error.main" : "text.primary" }}>
                              <Box component="span" sx={{ opacity: 0.4, mr: 0.75, userSelect: "none" }}>
                                {line.level === "error" ? "✕" : "›"}
                              </Box>
                              {line.message}
                            </Box>
                          ))
                        )}
                        <div ref={i === 0 ? logEndRef : null} />
                      </Box>

                      {i < uploadLogs.length - 1 && <Divider />}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </>
        )}

        {/* ════ MANAGE LAYERS TAB ════ */}
        {activeTab === 1 && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, gap: 1 }}>

            {/* selection action bar */}
            {selectedIds.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, bgcolor: "primary.50", border: 1, borderColor: "primary.200", borderRadius: 1, flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={500}>
                  {selectedIds.length} layer{selectedIds.length !== 1 ? "s" : ""} selected
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setSelectedIds([])} sx={{ textTransform: "none" }}>
                    Clear
                  </Button>
                  <Button
                    size="small" variant="contained" color="error"
                    disabled={deletingIds.length > 0}
                    startIcon={deletingIds.length > 0 ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                    onClick={deleteSelected}
                    sx={{ textTransform: "none" }}
                  >
                    {deletingIds.length > 0 ? "Deleting…" : `Delete (${selectedIds.length})`}
                  </Button>
                </Box>
              </Box>
            )}

            {/* table */}
            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: "primary.main" }}>
                      <Checkbox
                        size="small"
                        indeterminate={selectedIds.length > 0 && selectedIds.length < layerData.length}
                        checked={layerData.length > 0 && selectedIds.length === layerData.length}
                        onChange={toggleSelectAll}
                        sx={{ color: "white", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "white" } }}
                      />
                    </TableCell>
                    {["ID", "Name", "Geometry", "Processing Status", "Editable From Handset", "Actions"].map((h) => (
                      <TableCell key={h} align="center" sx={{ bgcolor: "primary.main", color: "white", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : layerData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <LayersIcon sx={{ fontSize: 40, opacity: 0.25, color: "text.disabled" }} />
                        <Typography variant="body2" color="text.disabled">No layers available</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    layerData.map((layer) => {
                      const isAdding = addingId === layer.id;
                      const isRemoving = removingId === layer.id;
                      const isToggling = togglingIds.includes(layer.id);
                      const isSelected = selectedIds.includes(layer.id);

                      return (
                        <TableRow
                          key={layer.id}
                          hover
                          selected={isSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox size="small" checked={isSelected} onChange={() => toggleSelect(layer.id)} />
                          </TableCell>

                          <TableCell align="center" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                            {layer.id}
                          </TableCell>

                          <TableCell align="left">{layer.name}</TableCell>

                          <TableCell align="center">
                            <Chip
                              label={Array.isArray(layer.geometry_types) ? layer.geometry_types.join(", ") : layer.geometry_types}
                              size="small" variant="outlined"
                            />
                          </TableCell>

                          <TableCell align="center">
                            <StatusChip status={layer.status} />
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title={editableMap[layer.id] ? "Make read-only" : "Make editable"}>
                              <span>
                                <Switch
                                  size="small"
                                  checked={editableMap[layer.id] ?? false}
                                  disabled={isToggling}
                                  onChange={() => handleEditableToggle(layer.id)}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title={layer.layer_added ? "Remove from map" : "Add to map"}>
                              <span>
                                <Button
                                  size="small" variant="contained"
                                  color={layer.layer_added ? "error" : "success"}
                                  disabled={isAdding || isRemoving}
                                  startIcon={
                                    isAdding || isRemoving
                                      ? <CircularProgress size={12} color="inherit" />
                                      : <PlaylistAddOutlinedIcon />
                                  }
                                  onClick={() =>
                                    layer.layer_added
                                      ? removeLayerFromMap(layer.id)
                                      : addLayerToMap(layer.id)
                                  }
                                  sx={{ textTransform: "none", fontSize: "0.72rem", minWidth: 80 }}
                                >
                                  {isAdding ? "Adding…" : isRemoving ? "Removing…" : layer.layer_added ? "Remove" : "Add"}
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0, pt: 0.5 }}>
                <Pagination
                  count={totalPages} page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary" size="small"
                  showFirstButton showLastButton
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={handleClose} disabled={uploading} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}